import {useState, useEffect} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import {Image} from '@jobseeker/lotus/components/Image';
import {Divider, Typography} from '@mui/material';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import Loader from '@jobseeker/lotus/components/Loader';
import {Box} from '@jobseeker/lotus/foundations';
import {fetchRecruiterJobs} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';

import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {usePageLoad} from '@hooks';
import {ROUTE_JOB_DETAIL} from '@constants';
import {InjectSearchOverlay, Chat} from '@components';

import type {AppState} from '@jobseeker/common-core/types';
import {VersionVariants} from '@jobseeker/lotus/enums';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';

const JobFeedHarness = dynamic(
  () => import('@jobseeker/client-harness/components/JobHarness/JobfeedV2'),
  {
    loading: () => <Loader />,
  }
);

const RecruiterPage = ({slug}: {slug: string}) => {
  const metaData = getConfigByApp(metaInfo, 'jobfeed', true);

  usePageLoad('jobfeed');

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const [pageNo, setPageNo] = useState<number>(1);
  // const [minexp, setMinexp] = useState<number>(0);
  // const [maxexp, setMaxexp] = useState<number>(0);
  const authStore = useSelector((state: AppState) => state.auth);
  // const jobDetailStore = useSelector((state: AppState) => state.jobDetail);

  useValidateAuth(dispatch);

  const {userId} = authStore || {};

  const router = useRouter();

  useEffect(() => {
    if (pageNo || pageNo === 0) {
      dispatch(
        fetchRecruiterJobs({
          userId,
          pageNo,
          recruiterId: slug.split('-').pop() || '',
        })
      );
      if (router.query.page?.toString() !== pageNo.toString()) {
        router.replace(
          {
            pathname: router.pathname,
            query: {...router.query, page: pageNo},
          },
          undefined,
          {shallow: true}
        );
      }
    }
  }, [pageNo, dispatch, userId, slug]);

  const routeToJobDetail = (jobSlug: string, jobId: string | number) => {
    router.push(`${ROUTE_JOB_DETAIL}/${jobSlug}-${jobId}`);
  };

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  const getSearchOverlay = () => {
    return <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />;
  };

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 20,
                textAlign: 'center',
                mb: 10,
              }}
            >
              <Typography sx={{mb: 1}} variant="caption">
                Last Active <b>21 Apr 2023</b>
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  display: 'inline-block',
                  lineHeight: '0',
                  padding: '5px',
                  borderRadius: '5px',
                }}
              >
                <Image
                  src={
                    'https://assets.gqindia.com/photos/5fdc6eb81c519f6c10ba2d2f/16:9/pass/Hrithik-Roshan%20(2).jpg'
                  }
                  alt=""
                  width={125}
                  height={125}
                  style={{objectFit: 'cover'}}
                />
              </Box>
              <Typography variant="h5" sx={{mt: 1}}>
                Hrithik Roshan
              </Typography>
              <Typography variant="body1">CEO at HRX</Typography>
              {/* <Link><Typography variant="body1">CEO at HRX</Typography></Link> */}
              <Typography>
                Michael Page International is a leading professional recruitment
                consultancy specialising in the recruitment of permanent,
                contract and temporary positions on behalf of the world&apos;s
                top employers.
              </Typography>
              <Box sx={{width: '100%'}}>
                <Divider />
              </Box>
              <Typography color="gray.dark">
                #CA#Sales#Finance and Accounts#Accounting#Marketing#Financial
                Reporting#Sales Head#Banking#B2B Sales#Analytics#Financial
                Analysis#Financial Control#Statistics#Corporate Sales#Financial
                Planning#Risk Management#Budgeting#Compliance#SAS#Financial
                Operations
              </Typography>
            </Box>
            <JobFeedHarness
              onLoadMore={() => setPageNo(pageNo + 1)}
              routeToJobDetail={routeToJobDetail}
              title={'My Jobfeed'}
              feedOnly={true}
              infiniteScroll={false}
              enablePagination={true}
              onPageChange={(page: number) => {
                setPageNo(page);
              }}
              currentPageNo={pageNo}
              injectSearchOverlay={getSearchOverlay}
              variant={VersionVariants.VERSION_TWO}
            />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export async function getServerSideProps(
  context: /* eslint-disable @typescript-eslint/no-explicit-any */ any
) {
  return {
    props: {
      slug: context.params.slug,
    },
  };
}

export default RecruiterPage;
